<script setup>
import { ref, computed, reactive } from 'vue';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  WORKFLOW_FLOW_VARIABLE_GROUPS,
  WORKFLOW_FLOW_VARIABLE_UPSTREAM_GROUP,
  WORKFLOW_VARIABLE_DRAG_TYPE,
  workflowOutputVariablesForNode,
} from './workflowFlowVariableCatalog';
import {
  resolveWorkflowToken,
  formatPinnedSampleValue,
} from './workflowEditorPinnedExecution';

const props = defineProps({
  pinnedExecution: {
    type: Object,
    default: null,
  },
  graphNodes: {
    type: Array,
    default: () => [],
  },
  graphEdges: {
    type: Array,
    default: () => [],
  },
  selectedNodeId: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['collapse', 'clearPinnedExecution']);

const TRIGGER_KIND_PT = {
  message_created: 'Mensagem recebida',
  contact_created: 'Contacto criado',
  conversation_created: 'Conversa criada',
  conversation_opened: 'Conversa aberta',
  conversation_resolved: 'Conversa resolvida',
  conversation_snoozed: 'Conversa adiada',
  scheduled_message_created: 'Mensagem agendada criada',
  scheduled_message_sent: 'Mensagem agendada enviada',
  scheduled_message_failed: 'Mensagem agendada falhou',
  label_added: 'Etiqueta adicionada',
  label_removed: 'Etiqueta removida',
  contact_updated: 'Contato atualizado',
  assignee_changed: 'Agente alterado',
  team_changed: 'Time alterado',
  kanban_item_created: 'Kanban: cartão criado',
  kanban_item_updated: 'Kanban: cartão atualizado',
  kanban_item_stage_changed: 'Kanban: mudou de etapa',
  kanban_item_deleted: 'Kanban: cartão excluído',
};

const ACTION_KIND_PT = {
  condition: 'Condição',
  stop_flow: 'Fim do fluxo',
  send_message: 'Enviar mensagem',
  add_private_note: 'Nota privada',
  add_label: 'Adicionar etiqueta',
  remove_label: 'Remover etiqueta',
  assign_agent: 'Atribuir agente',
  assign_team: 'Atribuir time',
  remove_agent: 'Remover agente',
  remove_team: 'Remover time',
  set_custom_attributes: 'Atributos da conversa',
  transfer_inbox: 'Transferir inbox',
  change_status: 'Alterar status',
  change_priority: 'Alterar prioridade',
  mute_conversation: 'Silenciar conversa',
  send_email_transcript: 'Enviar transcrição por e-mail',
  schedule_message: 'Agendar mensagem',
  wait: 'Esperar',
  http_request: 'Pedido HTTP',
  ai_agent: 'Agente de IA',
  kanban_create_item: 'Kanban: criar cartão',
  kanban_move_to_stage: 'Kanban: mover etapa',
  kanban_assign_agent: 'Kanban: atribuir agente',
  kanban_remove_agent: 'Kanban: remover agente',
  kanban_add_note: 'Kanban: nota',
  kanban_change_item_status: 'Kanban: status',
  kanban_start_timer: 'Kanban: iniciar timer',
  kanban_stop_timer: 'Kanban: parar timer',
};

const PANEL_COPY = {
  title: 'Dados entre nós',
  collapse: 'Ocultar painel de variáveis',
  description:
    'Use valores produzidos por outras partes do fluxo em campos de texto.',
  contextTitle: 'Contexto deste node',
  configuredFor: 'Configurando:',
  inputFrom: 'Entrada:',
  noInput: 'Nenhum node conectado antes deste passo.',
  fieldCount: 'campos',
  variable: 'Variável:',
  sample: 'Valor de exemplo',
  pinnedTitle: 'Referência da execução',
  executionPrefix: '#',
  separator: '·',
  pinnedDescription:
    'Os valores abaixo vêm desta execução fixada e servem apenas como pré-visualização.',
  clear: 'Limpar',
  searchLabel: 'Pesquisar variáveis',
  searchPlaceholder: 'Pesquisar por campo, origem ou token…',
  empty: 'Nenhuma variável corresponde à pesquisa.',
  noOutput: 'Este caminho ainda não tem uma saída estruturada disponível.',
};

const GROUP_ICONS = {
  upstream: 'i-lucide-git-branch',
  conversation: 'i-lucide-messages-square',
  contact: 'i-lucide-contact',
  message: 'i-lucide-message-square',
  workflow: 'i-lucide-workflow',
  account: 'i-lucide-building-2',
  kanban_item: 'i-lucide-columns-3',
};

const flowNodeKindTitle = node => {
  if (!node?.data) return 'Nó';
  const kind = node.data.kind || '';
  if (node.type === 'trigger') return TRIGGER_KIND_PT[kind] || kind;
  return ACTION_KIND_PT[kind] || kind;
};

const groupIcon = groupId => GROUP_ICONS[groupId] || 'i-lucide-database';

const variableSourceLabel = variable => {
  const labels = {
    http_request: 'Pedido HTTP',
    ai_agent: 'Agente de IA',
    condition: 'Condição',
    wait: 'Espera',
    stop_flow: 'Fim do fluxo',
    default: 'Ação anterior',
  };
  return labels[variable.sourceKind] || '';
};

const bindingsForSamples = computed(() => {
  const b = props.pinnedExecution?.bindings;
  if (b && typeof b === 'object' && !Array.isArray(b)) return b;
  return null;
});

const showPinnedBanner = computed(
  () =>
    bindingsForSamples.value &&
    Object.keys(bindingsForSamples.value).length > 0 &&
    props.pinnedExecution?.executionId != null
);

const searchQuery = ref('');

const normalizedQuery = computed(() => searchQuery.value.trim().toLowerCase());

const sampleLine = token => {
  if (!bindingsForSamples.value) return '';
  const raw = resolveWorkflowToken(bindingsForSamples.value, token);
  if (raw === null || raw === undefined) return '';
  return formatPinnedSampleValue(raw);
};

const selectedNode = computed(() =>
  props.graphNodes.find(
    node => node && String(node.id) === String(props.selectedNodeId)
  )
);

const selectedNodeTitle = computed(() => {
  const node = selectedNode.value;
  if (!node) return 'Node selecionado';
  const custom = String(node.data?.label || '').trim();
  return custom || flowNodeKindTitle(node);
});

const predecessorNodes = computed(() => {
  const targetId = props.selectedNodeId;
  const edges = props.graphEdges;
  const nodes = props.graphNodes;
  if (!targetId || !Array.isArray(edges) || !Array.isArray(nodes)) {
    return [];
  }
  const srcIds = [
    ...new Set(
      edges
        .filter(e => e && String(e.target) === String(targetId))
        .map(e => String(e.source))
    ),
  ];
  return srcIds.map(sid => {
    const n = nodes.find(x => x && String(x.id) === sid);
    return n || { id: sid, data: { label: sid, kind: '' } };
  });
});

const predecessorContextLine = computed(() => {
  if (!predecessorNodes.value.length) return PANEL_COPY.noInput;
  const labels = predecessorNodes.value.map(node => {
    const custom = String(node.data?.label || '').trim();
    return custom || flowNodeKindTitle(node);
  });
  return labels.join(' · ');
});

const dynamicUpstreamGroup = computed(() => {
  const predecessorActions = predecessorNodes.value.filter(
    node => node?.type === 'action'
  );
  const variables = predecessorActions.flatMap(workflowOutputVariablesForNode);
  const uniqueVariables = [
    ...new Map(variables.map(variable => [variable.token, variable])).values(),
  ];
  const labels = predecessorActions.map(node => {
    const custom = String(node.data?.label || '').trim();
    return custom || flowNodeKindTitle(node);
  });
  let label = WORKFLOW_FLOW_VARIABLE_UPSTREAM_GROUP.label;
  if (labels.length === 1) label = `Saída de ${labels[0]}`;
  if (labels.length > 1) label = 'Saídas dos nodes anteriores';

  return {
    ...WORKFLOW_FLOW_VARIABLE_UPSTREAM_GROUP,
    label,
    hint: predecessorActions.length
      ? 'Esses campos vêm do node executado imediatamente antes no caminho atual. O resultado é disponibilizado em upstream.output.'
      : WORKFLOW_FLOW_VARIABLE_UPSTREAM_GROUP.hint,
    emptyLabel: predecessorActions.length
      ? PANEL_COPY.noOutput
      : PANEL_COPY.noInput,
    variables: uniqueVariables,
  };
});

const variableGroups = computed(() => [
  dynamicUpstreamGroup.value,
  ...WORKFLOW_FLOW_VARIABLE_GROUPS.filter(group => group.id !== 'upstream'),
]);

const filteredGroups = computed(() => {
  const q = normalizedQuery.value;
  const groups = variableGroups.value;
  if (!q) return groups;

  const filtered = groups
    .map(group => {
      const gl = group.label.toLowerCase();
      const gh = (group.hint || '').toLowerCase();
      const vars = group.variables.filter(v => {
        const tl = v.label.toLowerCase();
        const tt = v.token.toLowerCase();
        const sample = sampleLine(v.token).toLowerCase();
        return (
          tl.includes(q) ||
          tt.includes(q) ||
          gl.includes(q) ||
          gh.includes(q) ||
          (sample && sample.includes(q))
        );
      });
      if (!vars.length) return null;
      return { ...group, variables: vars };
    })
    .filter(Boolean);

  return filtered.sort((a, b) => {
    if (a.id === 'upstream') return -1;
    if (b.id === 'upstream') return 1;
    return 0;
  });
});

const onVarDragStart = (event, token) => {
  event.dataTransfer.setData(WORKFLOW_VARIABLE_DRAG_TYPE, token);
  event.dataTransfer.setData('text/plain', token);
  event.dataTransfer.effectAllowed = 'copy';
};

const expandedGroups = reactive({});

const isGroupOpen = group =>
  Boolean(normalizedQuery.value) || expandedGroups[group.id] === true;

const toggleGroup = group => {
  expandedGroups[group.id] = !isGroupOpen(group);
};
</script>

<template>
  <aside
    class="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden bg-n-solid-2 text-n-slate-12"
  >
    <div class="shrink-0 border-b border-n-weak px-3 py-3">
      <div class="flex items-start justify-between gap-2">
        <h2
          class="min-w-0 flex-1 text-sm font-semibold leading-snug text-n-slate-12"
        >
          {{ PANEL_COPY.title }}
        </h2>
        <button
          type="button"
          class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-n-slate-11 transition-colors duration-200 ease-out hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9 motion-reduce:transition-none"
          :title="PANEL_COPY.collapse"
          @click="emit('collapse')"
        >
          <span
            class="i-lucide-chevrons-left size-4 shrink-0 rotate-180"
            aria-hidden="true"
          />
        </button>
      </div>
      <p class="mt-1 text-[11px] leading-snug text-n-slate-11">
        {{ PANEL_COPY.description }}
      </p>
      <div
        class="mt-3 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-2"
      >
        <div class="flex items-start gap-2">
          <span
            class="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md bg-n-teal-9/10 text-n-teal-11"
            aria-hidden="true"
          >
            <span class="i-lucide-route size-3.5" />
          </span>
          <div class="min-w-0">
            <p
              class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
            >
              {{ PANEL_COPY.contextTitle }}
            </p>
            <p class="mt-0.5 truncate text-xs text-n-slate-12">
              <span class="text-n-slate-11">{{
                PANEL_COPY.configuredFor
              }}</span>
              {{ selectedNodeTitle }}
            </p>
          </div>
        </div>
        <p
          class="mt-2 border-t border-n-weak pt-2 text-[11px] leading-snug text-n-slate-11"
        >
          <span class="font-medium text-n-slate-12">{{
            PANEL_COPY.inputFrom
          }}</span>
          {{ predecessorContextLine }}
        </p>
      </div>
      <div
        v-if="showPinnedBanner"
        class="mt-3 rounded-lg border border-n-teal-7/35 bg-n-teal-3/25 px-2.5 py-2"
      >
        <div class="flex items-start gap-2">
          <p class="min-w-0 flex-1 text-[11px] leading-snug text-n-slate-12">
            <span class="font-medium">{{ PANEL_COPY.pinnedTitle }}</span>
            {{ PANEL_COPY.executionPrefix }}{{ pinnedExecution.executionId }}
            <span class="text-n-slate-11">
              {{ PANEL_COPY.separator }} {{ PANEL_COPY.pinnedDescription }}
            </span>
          </p>
          <button
            type="button"
            class="inline-flex shrink-0 rounded-md px-2 py-1 text-[10px] font-medium text-n-teal-11 transition-colors hover:bg-n-alpha-2 hover:text-n-teal-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
            @click="emit('clearPinnedExecution')"
          >
            {{ PANEL_COPY.clear }}
          </button>
        </div>
      </div>
      <label class="sr-only" for="flow-variables-search">
        {{ PANEL_COPY.searchLabel }}
      </label>
      <Input
        id="flow-variables-search"
        v-model="searchQuery"
        type="search"
        autocomplete="off"
        size="sm"
        class="mt-3 w-full"
        :placeholder="PANEL_COPY.searchPlaceholder"
      />
    </div>

    <div
      class="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overflow-x-hidden overscroll-contain px-2 py-2 [scrollbar-width:thin] [-ms-overflow-style:auto] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-n-slate-8/40 [&::-webkit-scrollbar-track]:bg-transparent"
    >
      <template v-if="filteredGroups.length">
        <div
          v-for="group in filteredGroups"
          :key="group.id"
          class="overflow-hidden rounded-lg border border-n-weak bg-n-solid-1"
        >
          <button
            type="button"
            class="flex w-full items-center gap-2 px-2.5 py-2 text-left outline-none transition-colors hover:bg-n-alpha-2 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-n-teal-9"
            :aria-expanded="isGroupOpen(group)"
            @click="toggleGroup(group)"
          >
            <span
              class="flex size-6 shrink-0 items-center justify-center rounded-md bg-n-slate-3 text-n-slate-11"
              aria-hidden="true"
            >
              <span class="size-3.5" :class="groupIcon(group.id)" />
            </span>
            <span class="min-w-0 flex-1">
              <span
                class="block truncate text-xs font-semibold text-n-slate-12"
              >
                {{ group.label }}
              </span>
              <span class="block text-[10px] text-n-slate-10">
                {{ group.variables.length }} {{ PANEL_COPY.fieldCount }}
              </span>
            </span>
            <span
              class="size-4 shrink-0 text-n-slate-10"
              :class="
                isGroupOpen(group)
                  ? 'i-lucide-chevron-down'
                  : 'i-lucide-chevron-right'
              "
              aria-hidden="true"
            />
          </button>
          <div v-if="isGroupOpen(group)" class="border-t border-n-weak">
            <div
              v-if="group.hint"
              class="bg-n-slate-2 px-2.5 py-2 text-[10px] leading-snug text-n-slate-11"
            >
              {{ group.hint }}
            </div>
            <p
              v-if="!group.variables.length"
              class="border-t border-n-weak px-2.5 py-2 text-[10px] leading-snug text-n-slate-11"
            >
              {{ group.emptyLabel || PANEL_COPY.noOutput }}
            </p>
            <ul
              v-else
              class="list-none border-t border-n-weak px-1.5 pb-2 pt-1"
            >
              <li
                v-for="v in group.variables"
                :key="`${group.id}-${v.token}`"
                class="mt-1 first:mt-0"
              >
                <div
                  draggable="true"
                  class="cursor-grab rounded-md px-1.5 py-2 transition-colors hover:bg-n-alpha-2 active:cursor-grabbing"
                  @dragstart="onVarDragStart($event, v.token)"
                >
                  <div class="flex min-w-0 items-start gap-2">
                    <span
                      class="mt-0.5 size-3.5 shrink-0 text-n-slate-9 i-lucide-grip-vertical"
                      aria-hidden="true"
                    />
                    <div class="min-w-0 flex-1">
                      <div class="flex flex-wrap items-center gap-1.5">
                        <p
                          class="min-w-0 text-[11px] font-medium leading-snug text-n-slate-12"
                        >
                          {{ v.label }}
                        </p>
                        <span
                          v-if="variableSourceLabel(v)"
                          class="rounded bg-n-slate-3 px-1.5 py-0.5 text-[9px] font-medium text-n-slate-11"
                        >
                          {{ variableSourceLabel(v) }}
                        </span>
                      </div>
                      <p
                        class="mt-1 truncate font-mono text-[10px] leading-snug text-n-teal-11"
                        :title="v.token"
                      >
                        <span class="font-sans text-n-slate-10">{{
                          PANEL_COPY.variable
                        }}</span>
                        {{ v.token }}
                      </p>
                      <div v-if="sampleLine(v.token)" class="mt-1.5 max-w-full">
                        <p
                          class="mb-0.5 text-[9px] font-medium uppercase tracking-wide text-n-slate-10"
                        >
                          {{ PANEL_COPY.sample }}
                        </p>
                        <span
                          class="inline-block max-w-full rounded-md border border-n-teal-8/50 bg-n-teal-4/45 px-2 py-1 font-mono text-[10px] leading-snug text-n-teal-12 line-clamp-3 whitespace-pre-wrap break-words"
                          :title="sampleLine(v.token)"
                        >
                          {{ sampleLine(v.token) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </template>
      <p
        v-else
        class="px-2 py-4 text-center text-xs leading-snug text-n-slate-11"
      >
        {{ PANEL_COPY.empty }}
      </p>
    </div>
  </aside>
</template>
